import { createApiClient } from '@hauraki/api-client';
import { toast } from '../lib/toast';

/**
 * Shared `@hauraki/api-client` instance for the service files that have been
 * migrated onto it (`portal.ts`, `reading.ts` — see their file comments for
 * which files have NOT been migrated and why). The axios instance in
 * `api.ts` stays the transport for everything else; this is a second,
 * parallel client scoped to the migrated modules, not a replacement.
 *
 * Token read: same sessionStorage-scanning pattern already duplicated in
 * `teachingAgent.ts` and `useTutorStream.ts` (reads the OIDC
 * `oidc.user:<issuer>` key `react-oidc-context` writes) — reading directly
 * from storage avoids depending on `ApiBridge`'s effect having already run
 * (the exact race `hasValidOidcSession` in `api.ts` works around).
 *
 * 401 handling: no `refreshToken` is configured, matching `api.ts`'s axios
 * interceptor, which also does not attempt a refresh-then-retry — it goes
 * straight to the same toast + soft-redirect-via-custom-event on a 401.
 * `onUnauthorized` below reproduces that side effect exactly (including the
 * "don't clobber an already-recovered session" re-check) so behavior is
 * unchanged for any call migrated onto this client.
 */

export function getAccessToken(): string | null {
  try {
    const key = Object.keys(sessionStorage).find((k) => k.startsWith('oidc.user:'));
    if (!key) return null;
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const u = JSON.parse(raw) as { access_token?: string };
    return u.access_token ?? null;
  } catch {
    return null;
  }
}

function hasValidOidcSession(): boolean {
  try {
    const key = Object.keys(sessionStorage).find((k) => k.startsWith('oidc.user:'));
    if (!key) return false;
    const raw = sessionStorage.getItem(key);
    if (!raw) return false;
    const u = JSON.parse(raw) as { access_token?: string; expires_at?: number };
    if (!u.access_token) return false;
    const now = Math.floor(Date.now() / 1000);
    return typeof u.expires_at !== 'number' || u.expires_at > now;
  } catch {
    return false;
  }
}

function onUnauthorized(): void {
  if (hasValidOidcSession()) return;
  try {
    localStorage.removeItem('auth-storage');
  } catch {
    // localStorage can throw in private mode; ignore.
  }
  try {
    toast.error('Your session expired — please sign in again', { id: 'auth-session-expired' });
  } catch {
    // Toast failures must never break the redirect path.
  }
  try {
    window.dispatchEvent(new CustomEvent('auth:unauthorized'));
  } catch {
    // CustomEvent unsupported — fall through to the fallback below.
  }
  setTimeout(() => {
    if (hasValidOidcSession()) return;
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }, 1500);
}

export const apiClient = createApiClient({
  baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:3001',
  fetch: (...args) => fetch(...args),
  getAccessToken,
  onUnauthorized,
  isOnline: () => (typeof navigator === 'undefined' ? true : navigator.onLine),
});

export default apiClient;
